import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import NewsIndexClientStandalone from 'hb-react/frontoffice/apps/NewsIndex/startup/NewsIndexClientStandalone';
import {
  ROUTE_TAG_APP_PAGES,
  ROUTE_TAG_CURRENT_NETWORK,
  ROUTE_TAG_FEATURES,
} from 'hb-react/publicoffice/constants/routeTags';
import useNetworkConfiguration from 'hb-react/publicoffice/utils/useNetworkConfiguration';
import type { AppPageConfig, Colors, Network } from 'hb-react/types';

interface NewsAppNetwork {
  colors?: Partial<Colors>;
  theming?: Partial<Network>;
}

interface NewsAppClientStandaloneProps {
  poAsSPAEnabled: boolean;
  isIndexPage: boolean;
  network?: NewsAppNetwork;
  page?: Partial<AppPageConfig>;
}

const NewsAppClientStandalone = ({
  poAsSPAEnabled,
  isIndexPage = true,
  network,
  page,
}: NewsAppClientStandaloneProps) => {
  const { networkConfiguration } = useNetworkConfiguration({
    poAsSPAEnabled,
    styleNeeded: false,
    networkRouteTags: isIndexPage
      ? [
          {
            routeTag: ROUTE_TAG_APP_PAGES,
            params: { app_name: 'news' },
          },
          {
            routeTag: ROUTE_TAG_CURRENT_NETWORK,
          },
        ]
      : [
          {
            routeTag: ROUTE_TAG_FEATURES,
            // Brittle optimization:
            // Loading 'amplitude' to match the params from CookieBannerAppStandalone so both
            // queries will share the same queryKey
            params: { app_names: ['cookies', 'amplitude'] },
          },
          {
            routeTag: ROUTE_TAG_CURRENT_NETWORK,
          },
        ],
  });

  if (poAsSPAEnabled) {
    if (!networkConfiguration) {
      return (
        <Box alignItems="center" height="100vh" justifyContent="center">
          <Loader size={50} />
        </Box>
      );
    }

    const { colors, appPagesData, theming, featuresData, currentNetwork } =
      networkConfiguration;

    const networkProps = {
      title: currentNetwork?.title,
      colors: colors?.colors,
      theming: theming?.theming,
      ...featuresData,
    };
    const pageProps = appPagesData!;

    return (
      <NewsIndexClientStandalone network={networkProps} page={pageProps} />
    );
  }

  return (
    <NewsIndexClientStandalone
      network={network as NewsAppNetwork}
      page={page as Partial<AppPageConfig>}
    />
  );
};

export default NewsAppClientStandalone;
